import React from 'react';
import { useFormContext } from 'react-hook-form';
import type { SelfServiceFormData } from '../../../types/selfService';
import AddressLeadIn from './AddressLeadIn';
import DMASearchSelector from './DMASearchSelector';
import StateSelector from './StateSelector';
import RadiusGeoTargeting from './RadiusGeoTargeting';
import type { CampaignFeasibilityResponse } from '../../../services/feasibilityService';
import type { UseRadiusRecommendationResult } from '../../../services/useRadiusRecommendation';

type GeoMode = NonNullable<SelfServiceFormData['geoMode']>;

interface GeoTargetingTabsProps {
  accentColor: string;
  // DMA codes the publisher identified at onboarding. Surfaced as
  // "Suggested DMAs" quick-adds in the DMA tab so a local-station advertiser
  // doesn't have to scroll the full Nielsen list.
  suggestedDMACodes?: string[];
  // 2-letter state codes the publisher identified at onboarding (union
  // across their markets' coveredStates lists). Surfaced as "Suggested
  // states" chips in the State tab — covers border stations whose signal
  // spans multiple states.
  suggestedStateCodes?: string[];
  // CTV-489: home-market centroid derived from the station's first market.
  // Surfaced to AddressLeadIn as a one-click "Or use {label}" suggestion
  // chip on the Radius tab so the buyer can anchor the map without typing.
  homeMarketCenter?: { lat: number; lng: number; label: string } | null;
  // CTV-570 (radius-min): live feasibility verdict. Forwarded to
  // `RadiusGeoTargeting` so its slider min can be bumped to the
  // backend-computed smallest-feasible radius.
  feasibility?: CampaignFeasibilityResponse | null;
  // CTV-570 (radius-min v2): minimum-radius recommendation from the
  // dedicated endpoint; the slider bootstraps from this and re-syncs
  // whenever the six trigger inputs change.
  radiusRecommendation: UseRadiusRecommendationResult;
}

const TABS: { mode: GeoMode; label: string }[] = [
  { mode: 'radius', label: 'Radius' },
  { mode: 'states', label: 'States' },
  { mode: 'dmas', label: 'DMAs' },
];

// Tabbed picker for state / DMA / radius targeting. Each tab keeps its
// own selection list — the form persists all three so the user can flip back
// without losing work. The submit-time serializer (server-side) reads the
// active geoMode and the matching list.
// CTV-502: the standalone ZIP-codes tab was removed; ZIP targeting now goes
// through the Radius tool only.
const GeoTargetingTabs: React.FC<GeoTargetingTabsProps> = ({
  accentColor,
  suggestedDMACodes,
  suggestedStateCodes,
  homeMarketCenter,
  feasibility: _feasibility,
  radiusRecommendation,
}) => {
  const { watch, setValue } = useFormContext<SelfServiceFormData>();
  const mode = (watch('geoMode') ?? 'radius') as GeoMode;

  // Defense for draft resumes saved before geoMode got a form default — make
  // sure the visible tab is also what the submit serializer reads. Without
  // this, the radius tab is shown but the API falls back to publisher
  // markets because geoMode is undefined.
  React.useEffect(() => {
    if (!watch('geoMode')) {
      setValue('geoMode', 'radius', { shouldDirty: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedStates = watch('selectedStates') ?? [];
  const selectedDMAs = watch('selectedDMAs') ?? [];

  // CTV-533: empty-custom is now a resolver rule on `targetingMode`
  // (campaignSetupSchema.superRefine). RHF's `reValidateMode: 'onChange'`
  // re-runs the resolver and auto-clears the rose chip the moment any
  // tab gains a selection — no bandaid watcher needed here.
  // 2-letter state code derived from the geocoded business address. Passed
  // into StateSelector as a one-click suggestion; never auto-applied (so
  // entering just an address doesn't trip the conflict modal).
  const [addressDerivedState, setAddressDerivedState] = React.useState<string | null>(null);

  const setMode = (m: GeoMode) => setValue('geoMode', m, { shouldDirty: true });
  const setStates = (next: string[]) =>
    setValue('selectedStates', next, { shouldDirty: true });
  const setDMAs = (next: string[]) =>
    setValue('selectedDMAs', next, { shouldDirty: true });

  // The picker content swaps instantly when the tab changes, but we want the
  // surrounding card to grow/shrink smoothly between each tab's natural
  // height. Strategy: measure the inner content's scrollHeight via
  // ResizeObserver and drive the outer wrapper's `height` from there. Before
  // the first measurement we render with height:auto so SSR/initial paint
  // doesn't show a 0-height collapse.
  const innerRef = React.useRef<HTMLDivElement>(null);
  const [measuredHeight, setMeasuredHeight] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const update = () => setMeasuredHeight(el.scrollHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="mt-3 border border-slate-200 rounded-2xl bg-white p-4 space-y-4">
      <div role="tablist" aria-label="Targeting mode" className="flex gap-1 bg-slate-100 rounded-lg p-0.5 w-fit">
        {TABS.map((t) => {
          const active = mode === t.mode;
          return (
            <button
              key={t.mode}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setMode(t.mode)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                active ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
              style={active ? { color: accentColor } : undefined}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          height: measuredHeight ?? 'auto',
          transition: measuredHeight !== null ? 'height 220ms ease' : undefined,
        }}
      >
        <div ref={innerRef}>
          {mode === 'states' && (
            <StateSelector
              selectedStates={selectedStates}
              onChange={setStates}
              accentColor={accentColor}
              addressSuggestedStateCode={addressDerivedState}
              suggestedStateCodes={suggestedStateCodes}
            />
          )}

          {mode === 'dmas' && (
            <DMASearchSelector
              selectedDMAs={selectedDMAs}
              onChange={setDMAs}
              accentColor={accentColor}
              suggestedDMACodes={suggestedDMACodes}
            />
          )}

          {mode === 'radius' && (
            <div className="space-y-4">
              <AddressLeadIn
                accentColor={accentColor}
                onDerivedStateChange={setAddressDerivedState}
                suggestedCenter={homeMarketCenter ?? null}
              />
              <RadiusGeoTargeting accentColor={accentColor} radiusRecommendation={radiusRecommendation} />
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default GeoTargetingTabs;
